<template>
    <slot v-bind="{
        ...state,
        levels,
        benefits,
        setStickyRef,
        setScrollElement,
        setMembership,
        setPrice,
        selectedLevel,
        buyUrl,
        isLevelSelected,
        handleLevelSelect,
        handleLevelScroll,
        handleSubmit,
    }" />
</template>

<script setup>
import { computed, onBeforeMount, reactive } from 'vue';
import { debounce } from 'lodash';

import useSticky from '../utils/use-sticky';

const props = defineProps({
    memberships: {
        type: Array,
    },
    fundId: {
        type: Number,
        default: 161,
    },
    baseUrl: {
        type: String,
    },
});

const state = reactive({
    isLoading: true,
    memberships: [],
    membership: null,
    price: 0,
    showForm: false,
});

const { setStickyRef } = useSticky();

let scrollElements = [];

const setScrollElement = element => {
    if (element) {
        // add if not already tracked
        if (!scrollElements.includes(element)) {
            scrollElements.push(element);
        }
    } else {
        // remove any refs that are no longer in the DOM
        scrollElements = scrollElements.filter(node => {
            return document.body.contains(node);
        })
    }
}

const levels = computed(() => {
    return state.membership.levels;
});

const benefits = computed(() => {
    return state.membership.benefits.map(benefit => {
        const values = state.membership.levels.map((level, levelIndex) => {
            const direct = level.benefits.find(item => {
                return item.id === benefit.id;
            });

            if (direct) {
                return {
                    level,
                    hasValue: true,
                    hasValueOverride: !!direct.value,
                    valueOverride: direct.value ?? null,
                }
            }

            for (let i = levelIndex - 1; i >= 0; i--) {
                const prior = state.membership.levels[i].benefits.find(item => {
                    return item.id === benefit.id;
                });

                if (prior) {
                    return {
                        level,
                        hasValue: true,
                        hasValueOverride: !!prior.value,
                        valueOverride: prior.value ?? null,
                    }
                }
            }

            return {
                level,
                hasValue: false,
                hasValueOverride: false,
                valueOverride: null,
            };
        });

        return {
            ...benefit,
            values,
        }
    }).sort((a, b) => {
        return b.values.filter(v => v.hasValue).length - a.values.filter(v => v.hasValue).length;
    });
});

const selectedLevel = computed(() => {
    return state.membership.levels
        .slice()
        .sort((a, b) => {
            return Number(b.price) - Number(a.price);
        }).find(x => {
            return Number(x.price) <= Number(state.price);
        });
});

const setMembership = membership => {
    state.membership = membership;
    state.price = 0;
    state.showForm = false;

    const url = new URL(window.location.href);
    url.searchParams.set('tab', membership.slug);
    url.searchParams.delete('price');
    window.history.replaceState(null, '', url);
}

const setPrice = event => {
    state.price = parseInt(event.target.value, 10);
}

const isLevelSelected = level => {
    return selectedLevel.value?.id === level.id;
}

const handleLevelSelect = level => {
    state.price = level.price;
    state.showForm = true;

    const url = new URL(window.location.href);
    url.searchParams.set('price', level.price);
    window.history.replaceState(null, '', url);
}

const handleLevelScroll = debounce(event => {
    for (const element of scrollElements) {
        if (element === event.target) {
            continue;
        }

        element.scrollLeft = event.target.scrollLeft;
    }
}, 1);

const buyUrl = computed(() => {
    let fundId = props.fundId;

    if (selectedLevel.value) {
        fundId = selectedLevel.value.fund_id;
    } else if (state.membership) {
        fundId = state.membership.fund_id;
    }

    return `${props.baseUrl}/donate/${fundId}/add-to-basket/${state.price}`;
});

const handleSubmit = event => {
    event.preventDefault();

    if (!parseInt(state.price, 10)) {
        return alert('Please select a membership level or enter a gift amount');
    }

    if (parseInt(state.price, 10) < 10) {
        return alert('Minimum gift amount is $10');
    }

    window.location.href = buyUrl.value;
}

onBeforeMount(() => {
    if (!props.memberships.length) {
        return (state.isLoading = false);
    }

    state.memberships = props.memberships.filter(membership => {
        return membership.levels.length || membership.content_html;
    }).map(membership => {
        const benefitMap = new Map();

        for (const level of membership.levels) {
            for (const { id, description_html, content_html } of level.benefits) {
                if (benefitMap.has(id)) {
                    benefitMap.get(id).frequency++;
                } else {
                    benefitMap.set(id, { id, description_html, content_html, frequency: 1 });
                }
            }
        }

        const benefits = Array.from(benefitMap.values()).sort((a, b) => {
            return b.frequency - a.frequency;
        }).map(({ frequency, ...benefit }) => {
            return benefit;
        });

        return {
            ...membership,
            benefits,
        }
    });

    const searchParams = new URLSearchParams(window.location.search);
    const tabSlug = searchParams.get('tab');

    state.membership = (tabSlug && state.memberships.find(membership => membership.slug === tabSlug)) || state.memberships[0];

    const priceParam = parseInt(searchParams.get('price'), 10);
    const matchedLevel = priceParam && state.membership.levels.find(level => {
        return Number(level.price) === priceParam;
    });

    if (matchedLevel) {
        state.price = matchedLevel.price;
        state.showForm = true;
    } else {
        state.price = 0;
    }

    state.isLoading = false;
});
</script>
